import Dialog from "../../../../../AppDialog/components/Dialog/Dialog";
import { ChangeEvent, useEffect, useState } from "react";
import {
  answerToExercise,
  generateAIExercises,
} from "../../../../../../services/dictionary";
import quizIcon from "../../../../images/quiz.svg";
import "./ExerciseDialog.css";
import TalaeraSpinner from "../../../../../TalaeraSpinner/TalaeraSpinner";
import Dropdown from "../../../../../Dropdown/Dropdown";
import { ReactComponent as ArrowRightIcon } from "./../../../../../../images/arrow_right.svg";
import { ReactComponent as CorrectIcon } from "./../../../../../../images/correct.svg";
import { ReactComponent as InCorrectIcon } from "./../../../../../../images/incorrect.svg";
import { ReactComponent as CheckOutlineIcon } from "./../../../../../../images/check_circle_outline.svg";
import { ReactComponent as ErrorOutlineIcon } from "./../../../../../../images/cancel.svg";
import { ReactComponent as VisibilityIcon } from "./../../../../../../images/visibility.svg";
import { ReactComponent as VisibilityOffIcon } from "./../../../../../../images/visibility_off.svg";
import { ReactComponent as CheckIcon } from "./../../../../../../images/check-rounded.svg";
import { ReactComponent as TranslateIcon } from "./../../../../../../images/translate.svg";
import { ReactComponent as CloseIcon } from "./../../../../../../images/close.svg";
import { translate } from "../../../../../../services/translate";
import { ReactComponent as ForbiddenImg } from "images/403.svg";
import { ReactComponent as LockIcon } from "images/lock.svg";
import ProgressBar from "react-customizable-progressbar";
import { getErrorComponents } from "../../../../../../services/util";
import LanguageSelector from "../../../../../NativeLanguageSelector/NativeLanguageSelector";
import { VStack } from "components/Stack";
import { useNavigate } from "react-router-dom";
import TalaeraRoutes from "components/routes/TalaeraRoutes";
import Paywall from "../../../../../Paywall";
import useUser from "../../../../../../hooks/useUser";
import { useTranslation } from "react-i18next";

type Props = {
  smallVocab: boolean;
  open: boolean;
  onClose: () => void;
  explanation: string;
};

type Answer = {
  id: string;
  is_correct: boolean;
  word: string;
  explanation: string;
};

type ExampleSentence = {
  answers: Answer[];
  id: string;
  is_correct: boolean;
  sentence: string;
  target_word: string;
};

export const ExerciseDialog = ({ smallVocab, open, onClose }: Props) => {
  const user = useUser(true);

  const [exerciseId, setExerciseId] = useState<string | null>(null);
  const [exercises, setExercises] = useState<ExampleSentence[]>([]);
  const [step, setStep] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [stepAnswers, setStepAnswers] = useState<{ value: string; isCorrect: boolean }[]>(
    []
  );
  const [stepSubmitted, setStepSubmitted] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [translatedText, setTranslatedText] = useState("");
  const [error, setError] = useState<any>(null);
  const [targetLang, setTargetLang] = useState(user?.profile?.native_language);
  const [showLimitError, setShowLimitError] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const stepExercise = exercises && exercises[step];
  const stepAnswer =
    exercises &&
    exercises[step]?.answers.find(answer => answer.id === stepAnswers[step]?.value);
  const correctAnswer =
    exercises && exercises[step]?.answers.find(answer => answer.is_correct);
  const percentage = Math.floor(
    (stepAnswers.filter(answer => answer.isCorrect).length / exercises?.length) * 100
  );

  const loadExercise = async () => {
    setLoading(true);
    setError(null);
    for (let i = 0; i < 10; i++) {
      try {
        const response = await generateAIExercises(5);
        if (response.status === 202) {
          // Exercises are still generating and not ready yet. Continue again in 3 seconds.
          await new Promise(resolve => setTimeout(resolve, 3000));
          continue;
        } else if (response.status === 204) {
          setLoading(false);
          setError("You need to save more words in your personal dictionary first.");
          return;
        }
        setLoading(false);
        setExerciseId(response.body.id);
        setExercises(response.body.sentences);
        return;
      } catch (e: any) {
        if (e?.statusCode === 429) {
          setShowLimitError(true);
        } else {
          setError(e?.errors ?? e);
          setShowLimitError(false);
        }

        setLoading(false);
        return;
      }
    }
    setLoading(false);
    setError(
      "We're still generating exercises for you. Please try again in a couple of minutes."
    );
  };

  useEffect(() => {
    if (!smallVocab) {
      loadExercise();
    }
  }, [smallVocab]);

  const handleTranslateExercise = (lang: string) => {
    translate(
      {
        targetLang: lang,
        srcLang: "en",
        srcText: stepAnswer?.explanation,
      },
      (resp: any) => {
        setTranslatedText(resp.translated_text);
      },
      (err: any) => {
        setError(err);
      }
    );
  };

  const handleClickTranslate = (e: any) => {
    if (user?.profile?.native_language) {
      handleTranslateExercise(user?.profile?.native_language);
    } else {
      if (translatedText) {
        setTranslatedText("");
        e.stopPropagation();
      }
    }
  };

  const handlePracticeMore = () => {
    setShowLimitError(false);
    setStepAnswers([]);
    setStep(0);
    setExercises([]);
    setStepSubmitted(false);
    setShowResult(false);
    setShowAnswer(false);
    setTranslatedText("");
    setError(null);
    loadExercise();
  };

  const handleSubmitAnswer = () => {
    if (stepSubmitted) {
      if (step === exercises?.length - 1) {
        setShowResult(true);
      } else {
        setStep(step + 1);
        setStepSubmitted(false);
        setShowAnswer(false);
        setTranslatedText("");
      }
    } else {
      setStepSubmitted(true);
      answerToExercise(exerciseId, {
        answers: [
          {
            sentence_id: stepExercise?.id,
            answer_id: stepAnswer?.id,
          },
        ],
      });
    }
  };

  const handleChangeAnswer = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      const selectedAnswer =
        exercises &&
        exercises[step]?.answers.find(answer => answer.id === e.target.value);

      if (stepAnswers[step]) {
        setStepAnswers(
          stepAnswers.map((answer, index) =>
            index !== step
              ? answer
              : {
                  value: e.target.value,
                  isCorrect: !!selectedAnswer?.is_correct,
                }
          )
        );
      } else {
        setStepAnswers([
          ...stepAnswers,
          { value: e.target.value, isCorrect: !!selectedAnswer?.is_correct },
        ]);
      }
    }
  };

  const handleToggleShowAnswer = () => {
    setShowAnswer(!showAnswer);
  };

  const handleChangeTargetLang = (value: { code: string }) => {
    setTargetLang(value?.code);
    handleTranslateExercise(value?.code);
  };

  const hdUpgrade = () => {
    navigate(TalaeraRoutes.buildPlanDetailsPath());
    onClose();
  };

  return (
    <Dialog
      className="ai-generated-dialog"
      showDialog={open}
      closeOnOuterClick={false}
      onClose={onClose}
    >
      <button className="close-icon-button" onClick={onClose}>
        <CloseIcon />
      </button>

      <div className="ai-generated-dialog-header">
        <div>
          <img src={quizIcon} alt="quiz-icon" />
          <h3>{t("Library.PracticeMyVocab")}</h3>
        </div>
        {!smallVocab && !loading && !showLimitError && (
          <div>
            {t("Library.Question")} {step + 1} / {exercises?.length}
          </div>
        )}
      </div>

      {showLimitError && (
        <VStack align="center" className="limit-error">
          <ForbiddenImg />
          <h3>{t("Tool.oops")}</h3>
          <p>{t("Tool.haveMoreTomorrow")}</p>
          <hr />
        </VStack>
      )}

      {error && getErrorComponents(error)}

      {!error && !showLimitError && smallVocab && (
        <div className="ai-generated-less-vocab-warning">
          {t("Library.smallVocabError")}
        </div>
      )}

      {!smallVocab && !error && !showLimitError && (
        <div className="ai-generated-dialog-content">
          {showResult && (
            <div className="result-progress">
              <ProgressBar
                radius={130}
                progress={percentage}
                cut={120}
                rotate={-210}
                strokeWidth={6}
                strokeColor="#01BCD4"
                pointerStrokeColor="#01BCD4"
                trackStrokeWidth={6}
                pointerRadius={2}
              />
              <div className="progress-content">
                <CheckIcon />
                {t("Tool.Your Score")} {percentage}%
              </div>
            </div>
          )}

          {!showResult && (
            <>
              {loading ? (
                <div className="spinner">
                  <TalaeraSpinner />
                </div>
              ) : (
                <div className="example-sentence">
                  <span>{step + 1}.</span>
                  {exercises && exercises[step] && (
                    <>
                      <span>{exercises[step].sentence.split("___")[0]}</span>

                      {/* eslint-disable-next-line multiline-ternary */}
                      {stepSubmitted ? (
                        <span
                          className={`answer-result ${showAnswer || stepAnswer?.is_correct ? "correct" : "wrong"}`}
                        >
                          {showAnswer ? (
                            <>
                              <span>{stepAnswer?.word}</span>
                              <span>{correctAnswer?.word}</span>
                            </>
                          ) : (
                            stepAnswer?.word
                          )}
                          {(showAnswer || stepAnswer?.is_correct) && <CorrectIcon />}
                          {!showAnswer && !stepAnswer?.is_correct && <InCorrectIcon />}
                        </span>
                      ) : (
                        <Dropdown
                          value={stepAnswers[step]?.value}
                          onChange={handleChangeAnswer}
                        >
                          <option />
                          {stepExercise.answers.map((answerItem, index) => {
                            return (
                              <option key={index} value={answerItem.id}>
                                {answerItem.word}
                              </option>
                            );
                          })}
                        </Dropdown>
                      )}
                      <span>{exercises[step].sentence.split("___")[1]}</span>
                    </>
                  )}

                  {stepSubmitted && stepAnswer && (
                    <div
                      className={`result-explanation ${stepAnswer?.is_correct ? "correct" : "wrong"}`}
                    >
                      <div className="result-explanation-title">
                        {stepAnswer?.is_correct ? (
                          <CheckOutlineIcon />
                        ) : (
                          <ErrorOutlineIcon />
                        )}
                        {t("Library.AnswerKey")}
                      </div>
                      {translatedText ? translatedText : stepAnswer?.explanation}
                      <div className="translate-icon" onClick={handleClickTranslate}>
                        <TranslateIcon />
                      </div>
                      {!translatedText && !user?.profile?.native_language && (
                        <LanguageSelector
                          value={targetLang}
                          onChange={handleChangeTargetLang}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="ai-generated-dialog-action">
        {!smallVocab && showLimitError && !user?.student?.has_pro_access && (
          <Paywall
            triggerContainerClassName={"upgrade-text"}
            enable
            onSuccess={handlePracticeMore}
            onUpgrade={hdUpgrade}
          >
            <button className="btn practice-more locked">
              <LockIcon /> {t("Tool.Practice More")}
            </button>
          </Paywall>
        )}

        {!smallVocab && showResult && !showLimitError && (
          <button className="btn practice-more" onClick={handlePracticeMore}>
            {t("Tool.Practice More")}
          </button>
        )}

        {!smallVocab && (showResult || showLimitError) && (
          <button className="btn" onClick={onClose}>
            {t("Common.Done")}
          </button>
        )}

        {!smallVocab &&
          !showResult &&
          stepSubmitted &&
          !stepAnswer?.is_correct &&
          !loading && (
            <button className="show-answers" onClick={handleToggleShowAnswer}>
              {!showAnswer ? <VisibilityIcon /> : <VisibilityOffIcon />}{" "}
              {!showAnswer ? t("Library.ShowAnswers") : t("Library.HideAnswers")}
            </button>
          )}
        {!smallVocab && !showResult && !error && !showLimitError && !loading && (
          <button className="btn" onClick={handleSubmitAnswer}>
            {!stepSubmitted
              ? t("Library.ShowAnswers")
              : step === exercises?.length - 1
                ? t("Library.ShowFinalScore")
                : t("Library.NextQuestion")}
            {stepSubmitted && <ArrowRightIcon />}
          </button>
        )}
      </div>
    </Dialog>
  );
};
